import { debounce, each, max, range, throttle, isArray, sortBy } from "lodash";
import { fillContainer, findMaxHeight } from "./utils/layout";
import { safeParseJSON, zeroPadNumber } from "./utils/misc";
import { onSmallScreen } from "./breakpoint-detect";
import * as Hammer from "hammerjs";

declare const jQuery: any;

const $ = jQuery;
const $window = $(window);
const dfTransitionDuration = 0.5; // seconds
const dfRapidTransitionDuration = 0.1; // seconds (used for swipe gestures)
const dfWaitDuration = 5; // seconds
const dfTransitionType = "fade";
let heightFixDisabled = false;

export function init(): void
{
	$($ => {
		$(".banner").each(function () {
			const $banner = $(this);
			const minHeightData: MinHeightData|undefined = getMinHeightData($banner);
			setupSlideshow($banner);
			setupLayout($banner);
			setupHeightFix($banner, minHeightData);
		});
	});
}

type MinHeightData = [number, number][];

function getMinHeightData($banner): MinHeightData|undefined
{
	const val: string = $banner.attr("data-min-height");
	if (!val)
	{
		return undefined;
	}
	if (val[0] !== "[")
	{
		const minHeight: number = parseInt(val, 10) || undefined;
		if (!minHeight || minHeight < 1)
		{
			return undefined;
		}
		return [[0, minHeight]];
	}
	const data: MinHeightData = safeParseJSON(val);
	if (!isArray(data) || data.length === 0)
	{
		return undefined;
	}
	return sortBy(data, (item) => item[0]);
}

function enforceMinHeight(height: number, minHeightData: MinHeightData|undefined): number
{
	if (!minHeightData)
	{
		return height;
	}
	const viewportWidth: number = $window.outerWidth();
	let minHeight: number = 0;
	for (let item of minHeightData)
	{
		const itemViewportWidth: number = item[0];
		if (itemViewportWidth > viewportWidth)
		{
			break;
		}
		if (!item[1])
		{
			console.error("Bad banner min height data:", minHeightData);
			return height;
		}
		minHeight = item[1];
	}
	return max([minHeight, height]);
}

export function disableHeightFix(disabled: boolean = true): void
{
	heightFixDisabled = disabled;
}

function getTransitionDuration($banner): number
{
	const n: number = parseInt($banner.attr("data-transition-duration"), 10);
	return (n || dfTransitionDuration) * 1000;
}

function getRapidTransitionDuration($banner): number
{
	const n: number = parseFloat($banner.attr("data-rapid-transition-duration"));
	return (n || dfRapidTransitionDuration) * 1000;
}

function getWaitDuration($banner): number
{
	const n: number = parseInt($banner.attr("data-transition-wait"), 10);
	if (n === 0)
	{
		return 0;
	}
	return (n || dfWaitDuration) * 1000;
}

function getTransitionType($banner): string
{
	return $banner.attr("data-transition-type") || dfTransitionType;
}

function setupSlideshow($banner): void
{
	const $items = $banner.children(".banner-slideshow").children(".banner-slideshow-item");
	$items.first().addClass("current");
	const finalIndex: number = $items.length - 1;
	if (finalIndex < 1)
	{
		return;
	}

	const transitionDuration: number = getTransitionDuration($banner);
	const rapidTransitionDuration: number = getRapidTransitionDuration($banner);
	const waitDuration: number = getWaitDuration($banner);
	const transitionType: string = getTransitionType($banner);

	let currIndex = 0;
	let timer: any;
	let notifyOfMove: (newIndex: number) => void = undefined;
	let transitioning = false;

	const transitionResetSettings = initialiseForTransitions(transitionType, $items);

	const resetTransition = (): void => {
		if (transitionResetSettings.reset)
		{
			clearTimeout(timer);
			transitioning = true;
			currIndex = 0;
			transitionResetSettings.reset();
			transitioning = false;
			$items.removeClass("current").
				removeClass("transition-leaving").
				removeClass("transition-entering").
				first().addClass("current");
			if (notifyOfMove)
			{
				notifyOfMove(0);
			}
			waitThenNext();
		}
	};

	if (transitionResetSettings.panicOnResize)
	{
		$window.on("resize", throttle(resetTransition, 100, { leading: true }));
	}

	const waitThenNext = function waitThenNext(): void {
		clearTimeout(timer);
		if (waitDuration > 0)
		{
			timer = setTimeout(next, waitDuration);
		}
	};

	// Returns bool whether moved
	const move = function move(
		nextIndex: number,
		directionSym: "+"|"-",
		rapidTransition = false): boolean {
		if (transitioning || nextIndex === currIndex)
		{
			return false;
		}
		transitioning = true;
		$items.stop(true);
		const $old = $items.eq(currIndex);
		const $new = $items.eq(nextIndex);
		$old.addClass("transition-leaving").removeClass("current");
		$new.addClass("transition-entering");
		const duration: number = rapidTransition ? rapidTransitionDuration : transitionDuration;
		executeTransition(transitionType, duration, $old, $new, directionSym, () => {
			$old.removeClass("transition-leaving");
			$new.removeClass("transition-entering").addClass("current");
			currIndex = nextIndex;
			transitioning = false;
			waitThenNext();
		});
		if (notifyOfMove)
		{
			notifyOfMove(nextIndex);
		}
		$banner.trigger("changed");
		return true;
	};

	const forceRestart = function forceRestart(): boolean {
		clearTimeout(timer);
		$items.stop(true);
		transitioning = true;
		resetTransition();
		if (notifyOfMove)
		{
			notifyOfMove(0);
		}
		currIndex = 0;
		transitioning = false;
		waitThenNext();
		return true;
	};

	const next = function next(rapid = false): boolean {
		const nextIndex = (currIndex < finalIndex) ? currIndex + 1 : 0;
		return move(nextIndex, "+", rapid);
	};

	const prev = function prev(rapid = false): boolean {
		const nextIndex = (currIndex > 0) ? currIndex - 1 : finalIndex;
		return move(nextIndex, "-", rapid);
	};

	const controlMove = (nextIndex: number, rapid: boolean = false): void => {
		if (move(nextIndex, "+", rapid))
		{
			$banner.trigger("skipped");
		}
	};

	const controlPrev = (rapid: boolean = false): void => {
		if (prev(rapid))
		{
			$banner.trigger("skipped");
		}
	};

	const controlNext = (rapid: boolean = false): void => {
		if (next(rapid))
		{
			$banner.trigger("skipped");
		}
	};

	notifyOfMove = setupControls(
		$banner,
		controlPrev,
		controlNext,
		controlMove,
		$items.length
	);

	$banner.on("restart", () => {
		forceRestart();
	});

	if (!$banner.is("[data-wait-until-interaction]"))
	{
		waitThenNext();
	}
}

function initialiseForTransitions(transitionType: string, $items): { reset: () => void, panicOnResize: boolean }
{
	let reset: () => void = undefined;
	let panicOnResize: boolean = false;
	switch (transitionType)
	{
		case "slide":
			reset = function reset() {
				const slideWidth: number = $items.outerWidth();
				$items.each(function (index: number): void {
					$(this).css({
						left: index * slideWidth,
						right: "auto",
						width: "100%",
						backgroundAttachment: "scroll"
					});
				}).addClass("show");
			};
			panicOnResize = true;
			reset();
			break;

		case "slide_up":
			reset = function reset() {
				const slideHeight: number = $items.outerHeight();
				$items.each(function (index: number): void {
					$(this).css({
						top: index * slideHeight,
						bottom: "auto",
						height: "100%",
						backgroundAttachment: "scroll"
					});
				}).addClass("show");
			};
			panicOnResize = true;
			reset();
			break;

		case "fade":
		default:
			reset = function reset() {
				$items.eq(0).css({ opacity: 1, zIndex: 2 });
				$items.slice(1).css({ opacity: 0, zIndex: 0 }).addClass("show");
			};
			reset();
			break;
	}	
	return { reset, panicOnResize };
}

function executeTransition(
	transitionType: string,
	transitionDuration: number,
	$old,
	$new,
	directionSym: "+"|"-",
	afterTransition: () => void): void
{
	switch (transitionType)
	{
		case "slide":
			const slideWidth: number = $old.outerWidth();
			if (directionSym === "+")
			{
				$new.css({ left: slideWidth }).add($old).
					animate({ left: "-=" + slideWidth }, transitionDuration, afterTransition);
			}
			else
			{
				$new.css({ left: -slideWidth }).add($old).
					animate({ left: "+=" + slideWidth }, transitionDuration, afterTransition);
			}
			break;

		case "slide_up":
			const slideHeight: number = $old.outerHeight();
			if (directionSym === "+")
			{
				$new.css({ top: slideHeight }).add($old).
					animate({ top: "-=" + slideHeight }, transitionDuration, afterTransition);
			}
			else
			{
				$new.css({ top: -slideHeight }).add($old).
					animate({ top: "+=" + slideHeight }, transitionDuration, afterTransition);
			}
			break;

		case "fade":
		default:
			$new.css({ opacity: 1, zIndex: 1 });
			$old.animate({ opacity: 0 }, transitionDuration, () => {
				$old.css({ zIndex: 0 });
				$new.css({ zIndex: 2 });
				afterTransition();
			});
			break;
	}
}

function setupControls(
	$banner,
	prev: () => void,
	next: () => void,
	move: (index: number) => void,
	numImages: number): ((index: number) => void)|undefined
{
	const $dotWrap = $banner.find(".banner-slideshow-ctrl-dots").children().first();
	const $numbersWrap = $banner.find(".banner-slideshow-ctrl-numbers").children().first();
	const $chevrons = $banner.find(".banner-chevron");
	setupSwipeControls($banner, prev, next);
	if ($chevrons.length > 0)
	{
		setupChevrons($chevrons, prev, next);
	}
	if ($dotWrap.length > 0)
	{
		return setupDotControls($dotWrap, move, numImages);
	}
	if ($numbersWrap.length > 0)
	{
		return setupNumberControls($numbersWrap, move, numImages);
	}
	
	return undefined;
}

function setupDotControls($dotWrap, move: (index: number, rapid?: boolean) => void, numImages: number): (index: number) => void
{
	each(range(0, numImages), (index: number) => {
		const $dot = $(`<div class="dot">`);
		$dotWrap.append($dot);
		$dot.on("click", function onDotClick(ev) {
			move(index, true);
		});
	});
	const $dots = $dotWrap.find(".dot");
	$dots.first().addClass("selected");
	return (index: number): void => {
		$dots.removeClass("selected").eq(index).addClass("selected");
	};
}

function setupNumberControls($numbersWrap, move: (index: number, rapid?: boolean) => void, numImages: number): (index: number) => void
{
	each(range(0, numImages), (index: number) => {
		const $number = $(`<div class="number" data-index="${index}">${index + 1}</div>`);
		$numbersWrap.append($number);
		$number.on("click", function onDotClick(ev) {
			move(index, true);
		});
	});
	const $numbers = $numbersWrap.find(".number");
	$numbers.first().addClass("selected");
	if ($numbersWrap.parent().hasClass("zero-pad-numbers"))
	{
		$numbers.each(function () {
			const $number = $(this);
			const n: number = parseInt($number.html(), 10);
			if (!n) return;
			$number.html(zeroPadNumber(n));
		});
	}
	return (index: number): void => {
		$numbers.removeClass("selected").eq(index).addClass("selected");
	};
}

function setupSwipeControls($banner, prev: (rapid?: boolean) => void, next: (rapid?: boolean) => void): void
{
	const hammer = new Hammer($banner[0], {});
	const rapid = true;
	hammer.on("swiperight", () => {
		prev(rapid);
	});
	hammer.on("swipeleft", () => {
		next(rapid);
	});
}

function setupChevrons($chevrons, prev: (rapid?: boolean) => void, next: (rapid?: boolean) => void): void
{
	$chevrons.filter(".banner-chevron-prev").click((ev) => prev(true));
	$chevrons.filter(".banner-chevron-next").click((ev) => next(true));
}

function setupLayout($banner): void
{
	setupVideoIFrameDimensionsFix($banner);
}

function setupVideoIFrameDimensionsFix($banner): void
{
	const $iframe = $banner.find(".banner-video-embed iframe");
	if ($iframe.length < 1)
	{
		return;
	}
	const fix = function fix(): void {
		$iframe.css({
			width: "",
			height: "",
			minWidth: "",
			minHeight: "",
		});
		correctVideoIFrameDimensions($banner, $iframe)
	};
	fix();
	$(fix);
	$window.on("resize orientationchange load", debounce(fix, 300));
}

function correctVideoIFrameDimensions($banner, $iframe): void
{
	const w = parseInt($iframe.attr("width"), 10);
	const h = parseInt($iframe.attr("height"), 10);
	const cw = $banner.outerWidth();
	const ch = $banner.outerHeight();
	const { w: tw, h: th } = fillContainer(w, h, cw, ch);

	const dx = tw - cw;
	const dy = th - ch;

	$iframe.css({
		width: tw,
		height: th,
		minWidth: tw,
		minHeight: th,
		left: dx * -.5,
		top: dy * -.5
	});
}

function setupHeightFix($banner, minHeightData: MinHeightData|undefined): void
{
	const $elementsToResize = $banner.add($banner.find('.banner-slideshow-item,iframe'));
	const $mainHeader = $(".main-header");
	const fixedPositionHeader = () => {
		const pos: string = $mainHeader.css("position");
		return pos === "fixed" || pos === "absolute";
	};
	const isShortBanner: boolean = $banner.hasClass("banner-short");
	const $bannerContent = $banner.find(".banner-content");
	const $body = $("body");
	const $headerSpacer = $("#main-header-sticky-spacer");
	const getSpacerHeight = (): number => $headerSpacer.is(":visible") ? $headerSpacer.outerHeight() || 0 : 0;

	const fix = () => {
		if (heightFixDisabled)
		{
			return;
		}
		const headerIsFixedPosition = fixedPositionHeader();
		$bannerContent.css({
			paddingTop: (isShortBanner && headerIsFixedPosition) ? $mainHeader.outerHeight() : ""
		});
		$elementsToResize.css({
			height: "",
			minHeight: "",
			maxHeight: ""
		});
		let h = $banner.outerHeight();
		const spacerHeight: number = getSpacerHeight();
		if (!isShortBanner || onSmallScreen())
		{
			if (headerIsFixedPosition)
			{
				const bodyPaddingTop: number = parseInt($body.css("padding-top"), 10) || 0;
				h -= bodyPaddingTop;
				h -= spacerHeight;
			}
			else
			{
				h -= $mainHeader.outerHeight() || 0;
			}
			if (onSmallScreen())
			{
				h -= 50; // iOS fix
			}
		}

		const contentHeight: number = findMaxHeight($bannerContent.children()) +
			((headerIsFixedPosition && !spacerHeight) ?
				($mainHeader.outerHeight() * 2 + 30) :
				0);

		if (h < contentHeight)
		{
			h = contentHeight;
		}

		if (!h || h < 0)
		{
			return;
		}

		h = enforceMinHeight(h, minHeightData);

		$elementsToResize.css({
			height: h,
			minHeight: h,
			maxHeight: "none"
		});
	};
	fix();
	setTimeout(fix, 90);
	const throttledFix = throttle(fix, 60, { leading: true });
	let scrolledRecently = false;
	let scrolledRecentlyTimer: any;
	const setScrolledRecentlyFalseTimed = () => {
		clearTimeout(scrolledRecentlyTimer);
		scrolledRecentlyTimer = setTimeout(() => {
			scrolledRecently = false;
		}, 300);
	};
	$window.on("orientationchange load", throttledFix).on("resize", function () {
		if (!scrolledRecently)
		{
			throttledFix();
		}
	}).on("scroll", throttle(() => {
		scrolledRecently = true;
		setScrolledRecentlyFalseTimed();
	}, 40, { trailing: true }));

	$banner.on("set-min-height", (ev, data) => {
		data = data || {};
		if (data.hasOwnProperty("minHeightData"))
		{
			minHeightData = data.minHeightData;
			fix();
		}
	});
}

if (!window["iw"])
{
	window["iw"] = {};
}
window["iw"].banner = {};
window["iw"].banner.disableHeightFix = disableHeightFix;
